import React, { useState, useEffect } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../../Utils/loader";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  name: "",
  categoryName_ur: "",
  image: null,
  image_alt: "",
  image_title: "",
  status: true,
};

function AddCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState(EMPTY_FORM);
  const [nameError, setNameError] = useState("");
  const [imageError, setImageError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!modalOpen) {
      setCategoryData(EMPTY_FORM);
      setImagePreview(null);
      setNameError("");
      setImageError("");
      return;
    }

    if (category) {
      setCategoryData({
        name: category.name,
        categoryName_ur: category.categoryName_ur || "",
        image: category.image,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
        status: category.status,
      });
      setImagePreview(category.image || null);
    } else {
      setCategoryData(EMPTY_FORM);
      setImagePreview(null);
    }
    setNameError("");
    setImageError("");
  }, [modalOpen, category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value === "true" ? true : value === "false" ? false : value,
    }));
    if (name === "name" && nameError) {
      setNameError("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setCategoryData((prevData) => ({
          ...prevData,
          image: file,
        }));
        setImagePreview(event.target.result);
        setImageError("");
      };
      img.onerror = () => {
        setCategoryData((prevData) => ({
          ...prevData,
          image: null,
        }));
        setImagePreview(null);
        setImageError("Unable to read image. Please upload a valid PNG/JPEG.");
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setCategoryData((prevData) => ({
      ...prevData,
      image: null,
    }));
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let formValid = true;
    setNameError("");

    if (!categoryData.name) {
      setNameError("Category name is required.");
      formValid = false;
    }

    if (!categoryData.image) {
      setImageError((prev) => prev || "Image is required.");
      formValid = false;
    }

    if (imageError) {
      formValid = false;
    }

    if (!formValid) {
      return;
    }

    handleSaveCategory(categoryData, category ? category.id : null);
  };

  const handleCloseModal = () => {
    setCategoryData(EMPTY_FORM);
    setImagePreview(null);
    setNameError("");
    setImageError("");
    toggleModal();
  };

  const isEditMode = Boolean(category);
  const isFormValid =
    categoryData.name.trim().length >= 2 && categoryData.image;

  return (
    <UiModal
      isOpen={modalOpen}
      onClose={handleCloseModal}
      eyebrow="Categories"
      title={isEditMode ? "Edit Category" : "Add Category"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={handleCloseModal}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className="ui-btn-save min-w-[108px]"
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Category Image <span className="text-red-500">*</span>
          </label>

          <input
            id="category-image-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {imagePreview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={imagePreview}
                  alt="Category preview"
                  className="h-full w-full object-cover"
                />
              </div>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="category-image-upload"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="category-image-upload"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                Click to browse files
              </span>
            </label>
          )}

          {imageError ? (
            <p className="ui-form-error">{imageError}</p>
          ) : null}
          <p className="text-xs text-gray-500">
            Image is required for category creation
          </p>
          <ImageSeoFields
            imageAlt={categoryData.image_alt}
            imageTitle={categoryData.image_title}
            onAltChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_title: value }))
            }
          />
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Category details</h4>
        <div className="space-y-4">
          <div>
            <label htmlFor="category-name" className="ui-form-label">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              id="category-name"
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleInputChange}
              placeholder="Enter category name"
              className={`ui-input-field ${
                nameError ? "border-brand-error" : ""
              }`}
            />
            {nameError ? <p className="ui-form-error">{nameError}</p> : null}
            <p className="mt-1 text-xs text-gray-500">
              Minimum 2 characters required
            </p>
            <UrduInput
              label="زمرہ کا نام"
              name="categoryName_ur"
              value={categoryData.categoryName_ur}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label htmlFor="category-status" className="ui-form-label">
              Status
            </label>
            <select
              id="category-status"
              name="status"
              value={categoryData.status}
              onChange={handleInputChange}
              className="ui-select-field"
            >
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Default status is Active
            </p>
          </div>
        </div>
      </section>
    </UiModal>
  );
}

export default AddCategoryModal;
